import React from 'react';
import { motion } from 'framer-motion';
import { XIcon, SendIcon, ShieldIcon, CreditCardIcon, TrendingUpIcon, BellIcon, CheckCircleIcon, AlertTriangleIcon, GiftIcon } from 'lucide-react';
interface Notification {
  id: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  message: string;
  time: string;
  unread: boolean;
}
const NOTIFICATIONS: Notification[] = [{
  id: '1',
  icon: SendIcon,
  iconColor: '#22C55E',
  iconBg: '#22C55E15',
  title: 'Transfer Successful',
  message: 'Rs 5,000 sent to Asad Ali via Bank Alfalah',
  time: '2 min ago',
  unread: true
}, {
  id: '2',
  icon: ShieldIcon,
  iconColor: '#F37024',
  iconBg: '#F3702415',
  title: 'Security Alert',
  message: 'New login detected from Lahore. Was this you?',
  time: '15 min ago',
  unread: true
}, {
  id: '3',
  icon: CreditCardIcon,
  iconColor: '#661806',
  iconBg: '#66180615',
  title: 'Bill Due Reminder',
  message: 'Your electricity bill of Rs 3,200 is due tomorrow',
  time: '1 hour ago',
  unread: true
}, {
  id: '4',
  icon: GiftIcon,
  iconColor: '#8B5CF6',
  iconBg: '#8B5CF615',
  title: 'Cashback Earned!',
  message: 'You earned Rs 150 cashback on your last transaction',
  time: '3 hours ago',
  unread: false
}, {
  id: '5',
  icon: TrendingUpIcon,
  iconColor: '#3B82F6',
  iconBg: '#3B82F615',
  title: 'Savings Goal Update',
  message: 'You\'re 75% towards your "Emergency Fund" goal',
  time: '5 hours ago',
  unread: false
}, {
  id: '6',
  icon: CheckCircleIcon,
  iconColor: '#22C55E',
  iconBg: '#22C55E15',
  title: 'Payment Received',
  message: 'Rs 25,000 received from Rashid Khan',
  time: 'Yesterday',
  unread: false
}, {
  id: '7',
  icon: AlertTriangleIcon,
  iconColor: '#EAB308',
  iconBg: '#EAB30815',
  title: 'Low Balance Alert',
  message: 'Your account balance is below Rs 5,000',
  time: 'Yesterday',
  unread: false
}];
interface NotificationPanelProps {
  onClose: () => void;
  variant?: 'light' | 'dark';
}
export function NotificationPanel({
  onClose,
  variant = 'light'
}: NotificationPanelProps) {
  const isDark = variant === 'dark';
  const unreadCount = NOTIFICATIONS.filter(n => n.unread).length;
  return <>
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-40" style={{
      background: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.4)'
    }} onClick={onClose} data-id="element-474" />

      {/* Panel */}
      <motion.div initial={{
      opacity: 0,
      y: -20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -20,
      scale: 0.95
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 350
    }} className="fixed top-0 left-0 right-0 z-50 max-w-[430px] mx-auto" data-id="element-475">
        <div className="mx-3 mt-3 rounded-[24px] overflow-hidden shadow-2xl" style={{
        background: isDark ? 'rgba(30, 20, 14, 0.97)' : '#FFF7F3',
        backdropFilter: 'blur(20px)',
        border: isDark ? '1px solid rgba(255,247,243,0.08)' : '1px solid rgba(240,232,227,0.8)'
      }} data-id="element-476">
          {/* Header */}
          <div className="px-5 pt-5 pb-3 flex items-center justify-between" data-id="element-477">
            <div className="flex items-center gap-3" data-id="element-478">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{
              background: '#F3702420'
            }} data-id="element-479">
                <BellIcon className="w-4.5 h-4.5 text-[#F37024]" strokeWidth={2} data-id="element-480" />
              </div>
              <div data-id="element-481">
                <h3 className="text-[16px] font-bold" style={{
                color: isDark ? '#FFF7F3' : '#303030'
              }} data-id="element-482">
                  Notifications
                </h3>
                {unreadCount > 0 && <p className="text-[11px] font-semibold" style={{
                color: '#F37024'
              }} data-id="element-483">
                    {unreadCount} new
                  </p>}
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center transition-colors" style={{
            background: isDark ? 'rgba(255,247,243,0.08)' : '#F5EBE6'
          }} data-id="element-484">
              <XIcon className="w-4 h-4" style={{
              color: isDark ? '#FFF7F3' : '#661806'
            }} strokeWidth={2.5} data-id="element-485" />
            </button>
          </div>

          {/* Notification List */}
          <div className="max-h-[65vh] overflow-y-auto px-3 pb-4" data-id="element-486">
            {NOTIFICATIONS.map((notification, index) => {
            const Icon = notification.icon;
            return <motion.button key={notification.id} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05,
              duration: 0.25
            }} className="w-full flex items-start gap-3 p-3 rounded-2xl transition-colors text-left mb-0.5" style={{
              background: notification.unread ? isDark ? 'rgba(243,112,36,0.06)' : 'rgba(243,112,36,0.05)' : 'transparent'
            }} data-id="element-487">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{
                background: notification.iconBg
              }} data-id="element-488">
                    <Icon className="w-5 h-5" style={{
                  color: notification.iconColor
                }} strokeWidth={2} data-id="element-489" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-490">
                    <div className="flex items-start justify-between gap-2" data-id="element-491">
                      <p className="text-[13px] font-bold leading-tight" style={{
                    color: isDark ? '#FFF7F3' : '#303030'
                  }} data-id="element-492">
                        {notification.title}
                      </p>
                      {notification.unread && <div className="w-2 h-2 rounded-full bg-[#F37024] shrink-0 mt-1" data-id="element-493" />}
                    </div>
                    <p className="text-[12px] leading-snug mt-0.5" style={{
                  color: isDark ? 'rgba(255,247,243,0.5)' : '#818285'
                }} data-id="element-494">
                      {notification.message}
                    </p>
                    <p className="text-[10px] font-semibold mt-1.5 uppercase tracking-wider" style={{
                  color: isDark ? 'rgba(255,247,243,0.25)' : '#B8A99E'
                }} data-id="element-495">
                      {notification.time}
                    </p>
                  </div>
                </motion.button>;
          })}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t" style={{
          borderColor: isDark ? 'rgba(255,247,243,0.06)' : '#F0E8E3'
        }} data-id="element-496">
            <button className="w-full text-center text-[13px] font-bold py-2 rounded-xl transition-colors" style={{
            color: '#F37024',
            background: isDark ? 'rgba(243,112,36,0.08)' : 'rgba(243,112,36,0.06)'
          }} data-id="element-497">
              Mark all as read
            </button>
          </div>
        </div>
      </motion.div>
    </>;
}